import { useEffect, useState } from "react";
import Layout from "@/components/Layout";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  Table,
  TableHeader,
  TableRow,
  TableHead,
  TableBody,
  TableCell,
} from "@/components/ui/table";
import { Trophy, Medal, GraduationCap, Gauge, Gift, Download, Pencil } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";

const ROLE_LABEL = {
  company_admin: "Administrador",
  manager: "Gestor",
  supervisor: "Supervisor",
  operator: "Operador",
  auditor: "Auditor",
};

const initials = (name) =>
  (name || "?").split(" ").map((p) => p[0]).slice(0, 2).join("").toUpperCase();

const scoreColor = (v) =>
  v === null || v === undefined
    ? "text-stone-400"
    : v >= 80
    ? "text-emerald-700"
    : v >= 50
    ? "text-amber-600"
    : "text-rose-600";

const MEDAL_TONE = ["text-yellow-500", "text-stone-400", "text-amber-700"];

export default function TeamRanking() {
  const [rows, setRows] = useState(null);
  const [champions, setChampions] = useState([]);
  const [prize, setPrize] = useState(null);
  const [prizeOpen, setPrizeOpen] = useState(false);
  const [prizeForm, setPrizeForm] = useState({ title: "", description: "" });
  const [busy, setBusy] = useState(false);
  const { user } = useAuth();
  const canEditPrize = ["super_admin", "company_admin", "manager"].includes(user?.role);

  const loadPrize = () =>
    api
      .get("/gamification/prize")
      .then(({ data }) => setPrize(data.prize))
      .catch(() => {});

  useEffect(() => {
    api
      .get("/team/ranking")
      .then(({ data }) => setRows(data.ranking || []))
      .catch((err) => console.error("team ranking failed", err));
    api
      .get("/team/champions?months=6")
      .then(({ data }) => setChampions((data.champions || []).filter((c) => c.winner)))
      .catch(() => {});
    loadPrize();
  }, []);

  const savePrize = async () => {
    if (!prizeForm.title.trim()) {
      toast.error("Informe o título do prêmio");
      return;
    }
    setBusy(true);
    try {
      await api.put("/gamification/prize", prizeForm);
      toast.success("Prêmio do mês definido");
      setPrizeOpen(false);
      loadPrize();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Falha ao salvar prêmio");
    } finally {
      setBusy(false);
    }
  };

  const exportPdf = async () => {
    try {
      const res = await api.get("/team/ranking/export.pdf", { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = "ranking-equipe.pdf";
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Ranking exportado em PDF");
    } catch {
      toast.error("Falha ao exportar PDF");
    }
  };

  return (
    <Layout
      title="Ranking da Equipe"
      subtitle="Notas de pontualidade, treinamentos e classificação geral dos usuários"
      actions={
        <div className="flex flex-wrap gap-2">
          {canEditPrize && (
            <Button
              variant="outline"
              className="border-stone-300 bg-white"
              onClick={() => {
                setPrizeForm({
                  title: prize?.title || "",
                  description: prize?.description || "",
                });
                setPrizeOpen(true);
              }}
              data-testid="team-ranking-prize-btn"
            >
              <Gift className="h-4 w-4 mr-2" />
              {prize ? "Editar prêmio" : "Definir prêmio"}
            </Button>
          )}
          <Button
            className="bg-emerald-800 hover:bg-emerald-900 text-white"
            onClick={exportPdf}
            data-testid="team-ranking-export-pdf"
          >
            <Download className="h-4 w-4 mr-2" /> Exportar PDF
          </Button>
        </div>
      }
    >
      {prize && (
        <Card
          className="mb-4 p-4 rounded-xl border-yellow-300 bg-yellow-50/70 flex items-center gap-3"
          data-testid="team-ranking-prize-banner"
        >
          <div className="h-10 w-10 rounded-lg bg-yellow-400/30 text-yellow-700 flex items-center justify-center shrink-0">
            <Gift className="h-5 w-5" />
          </div>
          <div className="min-w-0 flex-1">
            <div className="font-display font-bold tracking-tight text-yellow-900">
              Prêmio do mês para o 1º lugar: {prize.title}
            </div>
            {prize.description && (
              <div className="text-xs text-yellow-800/80 font-body truncate">
                {prize.description}
              </div>
            )}
          </div>
          {canEditPrize && (
            <Button
              size="sm"
              variant="ghost"
              className="text-yellow-800 hover:bg-yellow-100 shrink-0"
              onClick={() => {
                setPrizeForm({ title: prize.title, description: prize.description || "" });
                setPrizeOpen(true);
              }}
              data-testid="team-ranking-prize-edit"
            >
              <Pencil className="h-3.5 w-3.5" />
            </Button>
          )}
        </Card>
      )}
      <Card
        className="rounded-xl border-stone-200 bg-white overflow-hidden"
        data-testid="team-ranking-page"
      >
        <Table>
          <TableHeader>
            <TableRow className="bg-stone-50">
              <TableHead className="font-body w-16">Pos.</TableHead>
              <TableHead className="font-body">Usuário</TableHead>
              <TableHead className="font-body text-center">Nota geral</TableHead>
              <TableHead className="font-body text-center">
                <span className="inline-flex items-center gap-1">
                  <Gauge className="h-3.5 w-3.5" /> Pontualidade (30d)
                </span>
              </TableHead>
              <TableHead className="font-body text-center">
                <span className="inline-flex items-center gap-1">
                  <GraduationCap className="h-3.5 w-3.5" /> Média treinamentos
                </span>
              </TableHead>
              <TableHead className="font-body text-center">Treinamentos pendentes</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows === null && (
              <TableRow>
                <TableCell colSpan={6} className="text-center text-stone-500 py-10 font-body text-sm">
                  Carregando…
                </TableCell>
              </TableRow>
            )}
            {rows?.length === 0 && (
              <TableRow>
                <TableCell colSpan={6} className="text-center text-stone-500 py-10">
                  <Trophy className="h-6 w-6 mx-auto mb-2 text-stone-400" />
                  Nenhum usuário para ranquear
                </TableCell>
              </TableRow>
            )}
            {(rows || []).map((r) => (
              <TableRow key={r.user_id} data-testid={`team-ranking-row-${r.user_id}`}>
                <TableCell>
                  <span className="inline-flex items-center gap-1 font-display font-bold">
                    {r.rank <= 3 ? (
                      <Medal className={`h-4 w-4 ${MEDAL_TONE[r.rank - 1]}`} />
                    ) : null}
                    {r.rank}º
                  </span>
                </TableCell>
                <TableCell>
                  <div className="flex items-center gap-2.5">
                    <Avatar className="h-8 w-8">
                      <AvatarImage src={r.picture || ""} alt={r.name} />
                      <AvatarFallback className="text-[10px] bg-emerald-700 text-white">
                        {initials(r.name)}
                      </AvatarFallback>
                    </Avatar>
                    <div className="min-w-0">
                      <div className="font-display font-semibold text-sm truncate">{r.name}</div>
                      <div className="text-[11px] text-stone-500 font-body">
                        {ROLE_LABEL[r.role] || r.role}
                      </div>
                    </div>
                  </div>
                </TableCell>
                <TableCell className="text-center">
                  <span
                    className={`font-display text-lg font-extrabold ${scoreColor(r.overall)}`}
                    data-testid={`team-ranking-overall-${r.user_id}`}
                  >
                    {r.overall ?? "—"}
                  </span>
                </TableCell>
                <TableCell className="text-center font-body text-sm">
                  <span className={`font-semibold ${scoreColor(r.punctuality)}`}>
                    {r.punctuality ?? "—"}
                  </span>
                  <div className="text-[10px] text-stone-400">
                    {r.on_time} no prazo · {r.late} atrasados · {r.missed} não exec.
                  </div>
                </TableCell>
                <TableCell className="text-center font-body text-sm">
                  <span className={`font-semibold ${scoreColor(r.trainings_avg)}`}>
                    {r.trainings_avg ?? "—"}
                  </span>
                  <div className="text-[10px] text-stone-400">{r.trainings_done} concluído(s)</div>
                </TableCell>
                <TableCell className="text-center">
                  {r.trainings_pending > 0 ? (
                    <Badge className="bg-amber-500 text-white border-none">
                      {r.trainings_pending} pendente(s)
                    </Badge>
                  ) : (
                    <Badge variant="outline" className="border-emerald-300 text-emerald-800 bg-emerald-50">
                      Em dia
                    </Badge>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>

      {/* Histórico de campeões dos meses anteriores */}
      {champions.length > 0 && (
        <div className="mt-6" data-testid="team-ranking-champions">
          <div className="mb-3">
            <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-emerald-800 font-body">
              Histórico
            </div>
            <h3 className="font-display text-xl font-bold tracking-tight">
              Campeões dos meses anteriores
            </h3>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {champions.map((c) => (
              <Card
                key={c.month}
                className="p-4 rounded-xl border-stone-200 bg-white flex items-center gap-3"
                data-testid={`champion-${c.month}`}
              >
                <div className="h-10 w-10 rounded-lg bg-yellow-400/20 text-yellow-600 flex items-center justify-center shrink-0">
                  <Trophy className="h-5 w-5" />
                </div>
                <Avatar className="h-9 w-9">
                  <AvatarImage src={c.winner.picture || ""} alt={c.winner.name} />
                  <AvatarFallback className="text-[10px] bg-emerald-700 text-white">
                    {initials(c.winner.name)}
                  </AvatarFallback>
                </Avatar>
                <div className="min-w-0 flex-1">
                  <div className="font-display font-semibold text-sm truncate">{c.winner.name}</div>
                  <div className="text-[11px] text-stone-500 font-body">
                    {c.label} · {c.winner.month_xp}% de aproveitamento
                  </div>
                  {c.prize_title && (
                    <div className="text-[11px] text-yellow-700 font-body truncate flex items-center gap-1">
                      <Gift className="h-3 w-3 shrink-0" /> {c.prize_title}
                    </div>
                  )}
                </div>
              </Card>
            ))}
          </div>
        </div>
      )}

      {/* Definir/editar prêmio do mês */}
      <Dialog open={prizeOpen} onOpenChange={setPrizeOpen}>
        <DialogContent className="max-w-md" data-testid="team-ranking-prize-dialog">
          <DialogHeader>
            <DialogTitle className="font-display flex items-center gap-2">
              <Gift className="h-5 w-5 text-yellow-600" /> Prêmio do mês
            </DialogTitle>
            <DialogDescription className="font-body text-xs text-stone-500">
              O prêmio é exibido para toda a equipe no dashboard e nesta página, motivando o 1º
              lugar do ranking.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Título *</Label>
              <Input
                value={prizeForm.title}
                onChange={(e) => setPrizeForm({ ...prizeForm, title: e.target.value })}
                placeholder="Ex: Vale-presente de R$ 100"
                className="mt-1 bg-white"
                data-testid="team-ranking-prize-title"
                autoFocus
              />
            </div>
            <div>
              <Label>Descrição (opcional)</Label>
              <Textarea
                rows={2}
                value={prizeForm.description}
                onChange={(e) => setPrizeForm({ ...prizeForm, description: e.target.value })}
                placeholder="Ex: entregue na reunião mensal de resultados"
                className="mt-1 bg-white"
                data-testid="team-ranking-prize-desc"
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              onClick={savePrize}
              disabled={busy}
              className="bg-emerald-800 hover:bg-emerald-900 text-white"
              data-testid="team-ranking-prize-save"
            >
              {busy ? "Salvando…" : "Salvar prêmio"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
